import { NonStrict } from '@berlin-front/common/utils';
import { type Ref, type defineComponent } from '@vue/composition-api';

export type RegisterFormDefault = {
  /**
   * 誕生日。
   * YYYY-MM-DD
   */
  birthday?: string;
  /**
   * emailアドレス。
   */
  email?: string;
  /**
   * 姓
   */
  customerFamilyName?: string;
  /**
   * 名
   */
  customerGivenName?: string;
  /**
   * せい
   */
  customerFamilyNameKana?: string;
  /**
   * めい
   */
  customerGivenNameKana?: string;
  /**
   * 性別
   *
   * 1. 男性
   * 2. 女性
   * 3. その他
   */
  sexDivision?: '1' | '2' | '3';
  /**
   * 郵便番号
   *
   * 7桁数字。
   */
  zipCode?: string;
  /**
   * 住所。
   *
   */
  address?: string;
  /**
   * 携帯番号
   *
   * 11桁数字。
   */
  phoneNumber?: string;
};

export type ServiceGlobals = {
  /**
   * requestId
   */
  requestId?: string;
  /**
   * サービスタイプ
   */
  serviceType: string | undefined;
  /**
   * エラー画面のルート名
   */
  errorRouteName: string;
  /**
   * 公開鍵。
   */
  pubkey: string | undefined;
  /**
   * ヘッダに表示すべきショップ名。
   */
  headerShopName: string;
  /**
   * ヘッダに表示すべき価格。
   * 数値ではなく例えば、状況に応じて価格未定と行った、表示すべき内容が設定される。
   */
  headerTotalPrice: string;
  /**
   * ヘッダに表示すべき価格単位。
   * 単位はフォントサイズとか変るため別になっている。
   */
  headerTotalPriceUnit: string;
  /**
   * ユーザ登録フォームのデフォルト補完に使用される値。
   */
  registerFormDefault?: RegisterFormDefault;
  /**
   * 加盟店側でユーザーを識別する番号
   */
  userNo?: string;
  /**
   * サービスタイプに紐づくヘルプコンポーネント
   */
  helpComponent?: ReturnType<typeof defineComponent>;
  /**
   * ヘルプの表示状態。
   */
  helpVisibility: Ref<boolean>;
  /**
   * ヘルプを表示する
   * @returns
   */
  showHelp: () => void;
  /**
   * ヘルプを隠す。
   * @returns
   */
  hideHelp: () => void;
};

/**
 * 個別のサービスタイプごとのメッセージハンドラ。
 * 特定のサービスタイプだけを処理するよう実装すること。
 */
type ServiceMessageHandler = {
  /**
   * 初期化メッセージを処理します。
   *
   * `parameter.service_type` を判別し、対象となるサービスタイプのデータのみを処理するよう実装してください。
   */
  init: (parameter: unknown) => void;

  /**
   * 決済開始です。
   *
   * 例えばatoneの場合はこのタイミングでモーダルが開きます。
   *
   * `parameter.service_type` を判別し、対象となるサービスタイプの情報が来た時、モーダルの起動処理を開始するよう実装します。
   * モーダルを起動するときのみ、戻り値を返すようにしてください。
   * 戻り値はグローバルステートで、ここに含めるのは、
   *
   * - サービスタイプに関わらず共通で必要となるような値。
   * - サービスタイプぺつに紐づけるデータソースが異なるが、型が同一となるデータ（共通で使用するため同一の型なのはあたりまえ）
   */
  start: (parameter: unknown) => void;

  /**
   * 入力されたサービスがこのハンドラで処理すべきserviceかどうかを判定する。
   *
   * 与えられたパラメータから、service_typeとmodal_modeをチェックすることで判定する。
   * @param service
   * @returns
   */
  check: (service: NonStrict<{ service_type: string; modal_mode?: string }>) => boolean;

  /**
   * currentサービスの切り替わりで差し替えられるRef
   */
  serviceGlobals: Ref<ServiceGlobals | undefined>;

  /**
   * サービスタイプ
   */
  serviceDiscriminator: {
    service_type: string;
    modal_mode: string | undefined;
  };
};

export type ServiceDiscriminator = { service_type: string; modal_mode?: string } | undefined;
type Service = (currentService: Ref<ServiceDiscriminator>) => ServiceMessageHandler;

export const services: Service[] = [];

const useRegisteredServices = (currentService: Ref<ServiceDiscriminator>): ServiceMessageHandler[] =>
  // 全部のservicesとModalBridgeでcurrentServiceを共有する。
  services.map((service) => service(currentService));
export default useRegisteredServices;
